import React, {useMemo} from 'react';
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {useWorkSiteStore} from "../../api/WorkSiteStore";
import {HibobOffice, WorkSite} from "../../api/Model";
import {hibobResidenceRows, HibobResidenceTable} from "./HibobResidenceTable";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {HibobResidenceSiteFormDialog} from "./HibobResidenceSiteFormDialog";
import {RemoteValue} from "../../util/RemoteValue";
import {useHibobOfficeStore} from "../../api/HibobOfficeStore";
import {HibobResidenceOfficeForm, HibobResidenceOfficeFormDialog} from "./HibobResidenceOfficeFormDialog";
import {usePermission} from "../../util/access/UseAccess";
import {WorkSites_Admin} from "../../util/access/Permission";

export default function HibobResidencePage() {
    const {remoteValue: sites, updateWorkSite} = useWorkSiteStore();
    const {remoteValue: offices, updateHibobOffice} = useHibobOfficeStore();
    const {canAdmin} = usePermission({canAdmin: WorkSites_Admin});
    const rows = useMemo(() => RemoteValue.combine({sites, offices})
        .map(({sites, offices}) => hibobResidenceRows(sites, offices)), [sites, offices]);

    const handleEditSite = useFormDialogAction({
        title: "Edit Work Site",
        description: "Update the work site information below.",
        submitButtonText: 'Update',
        renderForm: HibobResidenceSiteFormDialog,
        action: (openDialog, site: WorkSite) => openDialog({
            initialValue: {badge: site.badge, color: site.color, emoji: site.emoji},
            onSubmit: form => updateWorkSite(site, form),
            successMessage: `Updated work site ${site.name}.`,
            errorMessage: 'Failed to update work site. Please try again later.',
        }),
    }, [updateWorkSite]);

    const handleEditOffice = useFormDialogAction<HibobResidenceOfficeForm, [HibobOffice]>({
        title: "Edit Hibob Office",
        description: "Update the office badge, color, and parent work site.",
        fullWidth: true,
        submitButtonText: 'Save',
        renderForm: HibobResidenceOfficeFormDialog,
        action: async (openDialog, office) => openDialog({
            initialValue: {
                workSite: (await sites.value).find(site => site.id === office.workSiteId) ?? null,
                badge: office.badge,
                color: office.color,
            },
            onSubmit: form => updateHibobOffice(office, {
                workSiteId: form.workSite?.id ?? null,
                badge: form.badge,
                color: form.color,
            }),
            successMessage: `Updated Hibob office ${office.name}.`,
            errorMessage: 'Failed to update Hibob office. Please try again later.',
        }),
    }, [sites, updateHibobOffice]);

    return (
        <Page
            header="Hibob Residence"
            counter={<StorePageCount value={sites}/>}
            description="Edit badges and colors for work sites and Hibob offices, or assign an office to a work site."
        >
            <LoadingData value={rows} emptyMessage="No Hibob residences found."
                         defaultErrorMessage="Failed to load Hibob residences. Please try again later.">
                {rows => <HibobResidenceTable rows={rows} onEditSite={canAdmin ? handleEditSite : undefined}
                                             onEditOffice={canAdmin ? handleEditOffice : undefined}/>}
            </LoadingData>
        </Page>
    );
}
